import { useRef, useState } from 'react';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '../../shared/ui/popover';

/**
 * Apple SF Symbol `person.badge.plus` — inlined so the trailing pill
 * matches the iOS/macOS "add person" glyph exactly (lucide's UserPlus
 * has a different silhouette + badge geometry).
 */
function PersonBadgePlusIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 28.8965 27.2363"
      width={size}
      height={(size * 27.2363) / 28.8965}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M18.8549 15.91C18.4301 16.2192 18.0498 16.5851 17.7208 16.9939C16.7585 16.5745 15.6017 16.3086 14.2676 16.3086C9.78516 16.3086 7.30469 19.2969 7.30469 20.9082C7.30469 21.1133 7.41211 21.1816 7.66602 21.1816L16.3205 21.1816C16.3321 21.6925 16.4125 22.1865 16.5526 22.6562L8.125 22.6562C6.50391 22.6562 5.73242 22.168 5.73242 21.0938C5.73242 18.5352 8.96484 14.834 14.2676 14.834C16.0466 14.834 17.5928 15.2521 18.8549 15.91ZM18.4863 9.0332C18.4863 11.5527 16.5918 13.6133 14.2676 13.6133C11.9434 13.6133 10.0488 11.5625 10.0488 9.05273C10.0488 6.57227 11.9531 4.57031 14.2676 4.57031C16.6016 4.57031 18.4863 6.5332 18.4863 9.0332ZM11.6211 9.05273C11.6211 10.7812 12.8418 12.1387 14.2676 12.1387C15.7031 12.1387 16.9141 10.7617 16.9141 9.0332C16.9141 7.32422 15.7227 6.04492 14.2676 6.04492C12.8223 6.04492 11.6211 7.35352 11.6211 9.05273Z"
      />
      <path
        fill="currentColor"
        d="M27.5684 20.9473C27.5684 23.6523 25.293 25.9082 22.6074 25.9082C19.8828 25.9082 17.6465 23.6719 17.6465 20.9473C17.6465 18.2227 19.8828 15.9863 22.6074 15.9863C25.3223 15.9863 27.5684 18.2227 27.5684 20.9473ZM22.002 18.4375L22.002 20.3418L20.0977 20.3418C19.7363 20.3418 19.502 20.5762 19.502 20.9473C19.502 21.3086 19.7363 21.543 20.0977 21.543L22.002 21.543L22.002 23.457C22.002 23.8086 22.2363 24.0527 22.6074 24.0527C22.9688 24.0527 23.2031 23.8086 23.2031 23.457L23.2031 21.543L25.1074 21.543C25.4688 21.543 25.7129 21.3086 25.7129 20.9473C25.7129 20.5762 25.4688 20.3418 25.1074 20.3418L23.2031 20.3418L23.2031 18.4375C23.2031 18.0859 22.9688 17.8418 22.6074 17.8418C22.2363 17.8418 22.002 18.0859 22.002 18.4375Z"
      />
    </svg>
  );
}

/**
 * Glass-pill UserPlus button shown in the global UnifiedAppHeader
 * trailing area whenever the Add Project form is on the Employer
 * Details step. The pill is always visible on this step so the user
 * can see the affordance, but is disabled until an employer is
 * picked (no contractor → nothing meaningful to add a role/contact
 * to). Clicking opens a popover that consolidates the two
 * contact-management actions for that step:
 *
 *   • Add a role (Estimator / Site Manager / Quantity Surveyor) — the
 *     popover only lists role types not already shown in the form.
 *   • Create a new contact in the employer's contacts pool — opens
 *     the existing `AddContactDialog` via an event the form listens
 *     for.
 *
 * The header doesn't import `EmployerTab` directly; communication is
 * pure window events (`add-project:employer-state-change` from form
 * to header, `add-project:employer:*` back the other way) so this
 * component stays decoupled and the form's tab unmount doesn't
 * leave dead pills in the header.
 */
export interface AddProjectEmployerPillProps {
  hasContractor: boolean;
  inactiveRoles: Array<{ key: string; label: string }>;
  variant?: 'header' | 'inspector';
}

export function AddProjectEmployerPill({
  hasContractor,
  inactiveRoles,
  variant = 'header',
}: AddProjectEmployerPillProps) {
  const [open, setOpen] = useState(false);
  // Anchor the popover to the button via `PopoverAnchor virtualRef`
  // instead of `PopoverTrigger asChild` — the shared `popover.tsx`
  // wrapper runs `React.Children.map` on `asChild` children which
  // breaks Radix's `Slot.Children.only` invariant and silently
  // suppresses the trigger render. Pattern mirrors the projects
  // filter pill in `ProjectListView`.
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleAddRole = (key: string) => {
    window.dispatchEvent(new CustomEvent('add-project:employer:add-role', { detail: { key } }));
    setOpen(false);
  };

  const handleAddContact = () => {
    window.dispatchEvent(new CustomEvent('add-project:employer:add-contact'));
    setOpen(false);
  };

  return (
    <>
      {/* Glass pill — same recipe as the messages and bell pills
          (see `renderMobileHeaderAction` in UnifiedAppHeader). Two
          layers: a translucent BG span using
          `--header-pill-fill` / `--header-pill-blend` (white +
          multiply in light mode, translucent grey in dark), plus a
          foreground icon span on top. */}
      <button
        ref={buttonRef}
        type="button"
        aria-label="Add contact or role"
        data-testid="add-project-employer-pill"
        // Ties this pill into solid-chrome.css's existing `[data-header-action-id]`
        // treatment (icon-ink inversion, glass-bg flattening, toolbar sizing) —
        // the same recipe `renderMobileHeaderAction` pills get. Without it, this
        // custom pill's `var(--foreground)` icon (body-text black) is invisible
        // against the solid aubergine desktop toolbar (see solid-chrome.css's
        // "Labelled pills" and "Takeoff rail-hosted" comments for the same class
        // of bug in other custom header pills).
        data-header-action-id={variant === 'header' ? 'add-project-employer' : undefined}
        onClick={() => setOpen((prev) => !prev)}
        className={variant === 'inspector'
          ? 'relative flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground'
          : 'relative flex items-center justify-center active:opacity-75 transition-opacity'}
        style={variant === 'header' ? {
          height: 44,
          padding: '0 2px',
          borderRadius: 296,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          pointerEvents: 'auto',
        } : undefined}
      >
        {variant === 'header' && <span
          aria-hidden="true"
          className="pointer-events-none"
          style={{
            position: 'absolute',
            // 36 fill inside the 44 hit area, inset by the button's 2px padding
            // so it hugs the 36 content span. Matches
            // renderMobileHeaderAction — this pill shares the header row with
            // pills drawn by that path.
            left: 2,
            right: 2,
            height: 36,
            top: 'calc(50% - 18px)',
            zIndex: 0,
            borderRadius: 296,
            background: 'var(--header-pill-fill)',
            mixBlendMode: 'var(--header-pill-blend)' as any,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
          }}
        />}
        <span
          className="relative flex items-center justify-center"
          style={variant === 'header' ? { minWidth: 36, height: 36, zIndex: 1, color: 'var(--foreground)' } : undefined}
        >
          <PersonBadgePlusIcon size={variant === 'inspector' ? 19 : 24} />
        </span>
      </button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor virtualRef={buttonRef as any} />
        <PopoverContent surface="solid" align="end" sideOffset={6} className="w-[240px] p-1">
          {!hasContractor ? (
            <div
              className="px-3 py-3 font-apple text-center"
              style={{ fontSize: 'var(--text-menu)', color: 'var(--muted-foreground)' }}
            >
              Pick an employer first to add roles or contacts.
            </div>
          ) : (
            <>
              {inactiveRoles.length > 0 && (
                <>
                  <div
                    className="px-3 pt-2 pb-1 font-apple uppercase tracking-wider"
                    style={{ fontSize: '11px', color: 'var(--muted-foreground)' }}
                  >
                    Add Role
                  </div>
                  {inactiveRoles.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => handleAddRole(r.key)}
                      className="w-full text-left px-3 py-2 rounded-[var(--radius)] font-apple hover:bg-[color-mix(in_srgb,var(--popover-foreground)_8%,transparent)] transition-colors"
                      style={{ fontSize: 'var(--text-menu)', color: 'var(--popover-foreground)' }}
                    >
                      {r.label}
                    </button>
                  ))}
                  <div
                    className="my-1 mx-2"
                    style={{ borderTop: '1px solid var(--separator-nonopaque, var(--border))' }}
                  />
                </>
              )}
              <button
                type="button"
                onClick={handleAddContact}
                className="w-full text-left px-3 py-2 rounded-[var(--radius)] font-apple hover:bg-[color-mix(in_srgb,var(--popover-foreground)_8%,transparent)] transition-colors"
                style={{ fontSize: 'var(--text-menu)', color: 'var(--popover-foreground)' }}
              >
                Create new contact
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
